// Bulk scan: collect a run of labels into a basket, then apply one action to
// all of them at once.
//
// Reached from Inventory (?bulkscan=1). While a basket is active on the device,
// App.jsx also routes phone-camera label scans (?scan=TAG) here, so the basket
// fills whether the camera in this page or the camera app read the label.
//
// Three screens: scanning (camera live, one card per scan), review (the list
// and the action picker), and result (what happened, with undo). Nothing is
// written until Apply. Decommission is deliberately not offered in bulk.
import { useState, useEffect, useRef, useCallback } from 'react';
import jsQR from 'jsqr';
import api from '../../hooks/useApi';
import { useToast } from '../ui/Toast';
import SowSelect from '../shared/SowSelect';
import {
  extractAssetTag, readBasket, writeBasket, clearBasket, newBasket,
  addToBasket, removeFromBasket, describeAction, feedbackFor, scheduleTones, STATUS_LABELS,
} from '../../utils/bulkScan';

const BULK_ACTIONS = [
  { key: 'move',       label: 'Move to location' },
  { key: 'in_stock',   label: 'Set In Stock' },
  { key: 'in_transit', label: 'Set In Transit' },
  { key: 'allocated',  label: 'Allocate to SOW' },
  { key: 'deployed',   label: 'Set Deployed' },
];

// A label sitting in the viewfinder decodes on every frame; one read per label
// until it has been out of view for this long.
const REDECODE_MS = 2500;

export default function BulkScanPage({ scannedTag }) {
  const { toast } = useToast();
  const [basket, setBasket]       = useState(() => readBasket(localStorage) || writeBasket(localStorage, newBasket()));
  const [screen, setScreen]       = useState('scan');   // scan | review | done
  const [last, setLast]           = useState(null);     // { result, tag, item }
  const [soundBlocked, setSoundBlocked] = useState(false);   // a tone was refused: no tap has unlocked sound yet
  const [typed, setTyped]         = useState('');
  const [locations, setLocations] = useState([]);
  const [sows, setSows]           = useState([]);
  const [busy, setBusy]           = useState(false);
  const [actionKey, setActionKey] = useState(() => { try { return localStorage.getItem('av_bulk_scan_action') || 'move'; } catch (e) { return 'move'; } });
  const [moveLoc, setMoveLoc]     = useState(() => { try { return localStorage.getItem('av_last_inv_location') || ''; } catch (e) { return ''; } });
  const [alsoInStock, setAlsoInStock] = useState(false);
  const [sowId, setSowId]         = useState('');
  const [notes, setNotes]         = useState('');
  const [outcome, setOutcome]     = useState(null);     // { batch_id, reference, applied, skipped, summary, undone }
  const handledTag = useRef(null);
  // The basket as it is now, not as it was when a lookup started. Labels scanned
  // faster than lookups return each add to this, so one cannot overwrite another.
  const basketRef = useRef(basket);

  useEffect(() => {
    api.get('inventory/locations').then(setLocations).catch(() => setLocations([]));
    api.get('sows', { status: 'Active' }).then(setSows).catch(() => setSows([]));
  }, []);

  const persist = useCallback((next) => {
    basketRef.current = writeBasket(localStorage, next);
    setBasket(basketRef.current);
  }, []);
  const beep = useCallback((result) => { signal(result).then(ok => setSoundBlocked(!ok)); }, []);

  // Look a tag up and drop it in the basket. Read-only: nothing changes on the server.
  const scanTag = useCallback(async (raw) => {
    const tag = extractAssetTag(raw);
    if (!tag) return;
    if (basketRef.current.items.some(i => i.asset_tag === tag)) {
      setLast({ result: 'duplicate', tag });
      beep('duplicate');
      return;
    }
    try {
      const item = await api.get(`inventory/by-tag/${encodeURIComponent(tag)}`);
      const { basket: next, result } = addToBasket(basketRef.current, {
        id: item.id, asset_tag: item.asset_tag || tag,
        name: item.catalog_description || item.name || '',
        location_name: item.location_name || '', status: item.status || '',
        sow_reference: item.allocated_sow_reference || '',
      });
      persist(next);
      setLast({ result, tag: item.asset_tag || tag, item });
      beep(result);
    } catch (e) {
      setLast({ result: 'not_found', tag });
      beep('not_found');
    }
  }, [persist, beep]);

  // A label read by the camera app arrives as ?scan=TAG: take it once, then
  // return the URL to the plain basket page so a reload does not re-add it.
  useEffect(() => {
    if (scannedTag && handledTag.current !== scannedTag) {
      handledTag.current = scannedTag;
      scanTag(scannedTag);
      try { window.history.replaceState(null, '', '/?bulkscan=1'); } catch (e) { /* ignore */ }
    }
  }, [scannedTag, scanTag]);

  const submitTyped = (e) => {
    e.preventDefault();
    const t = typed;
    setTyped('');
    scanTag(t);
  };

  const action = buildAction({ actionKey, moveLoc, alsoInStock, sowId });
  const actionText = describeAction(action, { locations, sows });
  const needsSowOrReason = (actionKey === 'allocated' || actionKey === 'deployed') && !sowId && !notes.trim();

  const apply = async () => {
    if (!basket.items.length) { toast.error('Nothing in the basket yet.'); return; }
    if (actionKey === 'move' && !moveLoc) { toast.error('Pick a location, or choose “Unknown / no location”.'); return; }
    if (needsSowOrReason) { toast.error('Link a SOW or give a reason.'); return; }
    setBusy(true);
    try {
      const res = await api.post('inventory/bulk-scan', {
        asset_tags: basket.items.map(i => i.asset_tag),
        action,
        notes: notes.trim() || undefined,
      });
      try {
        if (action.location_id) localStorage.setItem('av_last_inv_location', action.location_id);
        localStorage.setItem('av_bulk_scan_action', actionKey);
      } catch (e) { /* ignore */ }
      setOutcome({ ...res, summary: actionText, undone: false });
      clearBasket(localStorage);
      setScreen('done');
    } catch (e) {
      toast.error('Could not apply: ' + (e.message || 'unknown error'));
    }
    setBusy(false);
  };

  const undo = async () => {
    if (!outcome?.batch_id) return;
    setBusy(true);
    try {
      const r = await api.post(`inventory/bulk-scan/${outcome.batch_id}/undo`, {});
      setOutcome(o => ({ ...o, undone: true, restored: r.restored }));
    } catch (e) {
      toast.error('Could not undo: ' + (e.message || 'unknown error'));
    }
    setBusy(false);
  };

  const startAnother = () => {
    persist(newBasket());
    setLast(null);
    setOutcome(null);
    setScreen('scan');
  };

  const exit = () => {
    clearBasket(localStorage);
    window.location.href = '/#inventory';
  };

  const cancelBasket = () => {
    if (basket.items.length && !window.confirm(`Discard ${basket.items.length} scanned item${basket.items.length === 1 ? '' : 's'} without applying anything?`)) return;
    exit();
  };

  return (
    // A phone-width column on a phone. From md up (a tablet, a laptop) the page
    // takes the whole screen with larger type, and from lg up it is held to the
    // screen's height so the camera can sit beside the basket and fill it.
    <div className="min-h-screen lg:h-dvh bg-gray-50 text-navy" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="max-w-md md:max-w-none mx-auto min-h-screen lg:min-h-0 lg:h-full flex flex-col bg-white shadow-sm">
        {screen === 'scan' && (
          <ScanScreen
            basket={basket} last={last} typed={typed} setTyped={setTyped} submitTyped={submitTyped}
            soundBlocked={soundBlocked} onUnlockSound={() => { primeAudio(); setSoundBlocked(false); }}
            onDecode={scanTag} onReview={() => setScreen('review')} onCancel={cancelBasket}
            onRemove={(tag) => persist(removeFromBasket(basketRef.current, tag))}
          />
        )}
        {screen === 'review' && (
          <ReviewScreen
            basket={basket} locations={locations} sows={sows}
            actionKey={actionKey} setActionKey={setActionKey}
            moveLoc={moveLoc} setMoveLoc={setMoveLoc} alsoInStock={alsoInStock} setAlsoInStock={setAlsoInStock}
            sowId={sowId} setSowId={setSowId} notes={notes} setNotes={setNotes}
            needsSowOrReason={needsSowOrReason} actionText={actionText} busy={busy}
            onBack={() => setScreen('scan')} onRemove={(tag) => persist(removeFromBasket(basketRef.current, tag))} onApply={apply}
          />
        )}
        {screen === 'done' && (
          <DoneScreen outcome={outcome} busy={busy} onUndo={undo} onAnother={startAnother} onExit={exit} />
        )}
      </div>
    </div>
  );
}

function buildAction({ actionKey, moveLoc, alsoInStock, sowId }) {
  if (actionKey === 'move') {
    const a = { location_id: moveLoc === '__none__' ? null : (moveLoc || undefined) };
    if (alsoInStock) a.status = 'in_stock';
    return a;
  }
  const a = { status: actionKey };
  if ((actionKey === 'allocated' || actionKey === 'deployed') && sowId) a.allocated_to_sow_id = sowId;
  return a;
}

// ── Sound and vibration on each scan ─────────────────────────────────────────
// Tones are Web Audio oscillators on one shared context. iOS only starts a
// context inside a completed tap (touchend or click, not touchstart), so the
// start tap and any later tap resume it; once started it can be resumed from
// anywhere. iOS mutes Web Audio while the ringer switch is on silent, which on
// a work phone it usually is, unless the page asks for the playback audio
// session (iOS 17 and later), so that is requested before the context exists.
// Generated WAV clips in <audio> elements were tried before this and played
// silently on a real iPhone. iPhones have no vibration API.
let ctx = null;
function audioContext() {
  if (ctx) return ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try { if (navigator.audioSession) navigator.audioSession.type = 'playback'; } catch (e) { /* older iOS */ }
  try { ctx = new AC(); } catch (e) { return null; }
  return ctx;
}
function primeAudio() {
  const c = audioContext();
  if (c && c.state !== 'running') c.resume().catch(() => {});
}
if (typeof window !== 'undefined') {
  for (const ev of ['touchend', 'click', 'keydown']) window.addEventListener(ev, primeAudio, { passive: true });
}

// Resolves false when the context will not run, which means no tap has
// unlocked sound yet, so the screen can offer one. A browser with no Web Audio
// at all is reported as fine: the result card still shows the outcome.
async function playTones(tones) {
  const c = audioContext();
  if (!c) return true;
  if (c.state !== 'running') {
    // resume() can stay pending for good on iOS when no tap has unlocked sound.
    await Promise.race([c.resume().catch(() => {}), new Promise(r => setTimeout(r, 300))]);
  }
  if (c.state !== 'running') return false;
  try { scheduleTones(c, tones); } catch (e) { /* the screen still shows the result */ }
  return true;
}

function signal(result) {
  const fb = feedbackFor(result);
  try { if (navigator.vibrate) navigator.vibrate(fb.vibrate); } catch (e) { /* ignore */ }
  return playTones(fb.tones);
}

// ── Header shared by the three screens ───────────────────────────────────────
function Header({ title, sub, right, onLeft, leftIcon }) {
  return (
    <div className="px-4 py-3 md:px-6 md:py-4 flex items-center justify-between text-white" style={{ background: 'var(--navy)' }}>
      <div className="flex items-center gap-3 min-w-0">
        {onLeft && (
          <button onClick={onLeft} aria-label={leftIcon === 'x' ? 'Close' : 'Back'} className="text-white/80 hover:text-white text-lg md:text-2xl md:p-2 leading-none">
            {leftIcon === 'x' ? '✕' : '←'}
          </button>
        )}
        <div className="min-w-0">
          <div className="text-sm md:text-lg font-semibold truncate">{title}</div>
          {sub && <div className="text-[10px] md:text-xs truncate" style={{ color: '#E5B87A' }}>{sub}</div>}
        </div>
      </div>
      {right}
    </div>
  );
}

// ── Screen 1: scanning ───────────────────────────────────────────────────────
function ScanScreen({ basket, last, typed, setTyped, submitTyped, soundBlocked, onUnlockSound, onDecode, onReview, onCancel, onRemove }) {
  const count = basket.items.length;
  return (
    <>
      <Header title="Bulk scan" sub="Basket on this device · nothing saved yet" onLeft={onCancel} leftIcon="x"
        right={(
          <div className="flex items-center gap-3">
            {/* A long basket pushes the bottom Review button off screen; this one is always to hand */}
            <button onClick={onReview} disabled={!count}
              className="text-white text-[10px] md:text-sm font-semibold border border-white/40 rounded px-2 py-1 md:px-4 md:py-2 disabled:opacity-40">
              Review →
            </button>
            <div className="text-2xl md:text-3xl font-semibold tabular-nums">{count}</div>
          </div>
        )} />
      {/* Stacked on a phone; from lg up the camera fills the left and the basket sits beside it */}
      <div className="flex-1 flex flex-col lg:flex-row lg:min-h-0">
        <div className="lg:flex-1 lg:min-w-0 lg:flex lg:flex-col">
          <CameraView onDecode={onDecode} />
        </div>
        <div className="px-4 pt-3 md:px-6 md:pt-4 flex-1 flex flex-col lg:flex-none lg:w-[26rem] lg:border-l border-gray-200 lg:overflow-y-auto">
          {soundBlocked && (
            <button type="button" onClick={onUnlockSound}
              className="mb-2 w-full py-2 md:py-3 rounded-lg bg-amber-50 border border-amber-200 text-[11px] md:text-sm font-semibold text-amber-800">
              🔇 Tap for sound on each scan
            </button>
          )}
          <LastScan last={last} />
          <form onSubmit={submitTyped} className="flex gap-2 mt-2">
            <input value={typed} onChange={e => setTyped(e.target.value)} placeholder="Type a tag, e.g. AV-INV-00312"
              autoComplete="off" autoCapitalize="characters" inputMode="text"
              className="form-input text-[12px] md:text-base flex-1" />
            <button type="submit" className="px-3 py-2 md:px-5 border border-gray-300 rounded text-[11px] md:text-sm font-semibold text-navy hover:bg-gray-50">Add</button>
          </form>
          {count > 0 && (
            <div className="mt-3">
              <div className="text-[10px] md:text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Recent</div>
              {basket.items.slice(0, 5).map(i => (
                <BasketRow key={i.asset_tag} item={i} onRemove={onRemove} />
              ))}
              {count > 5 && <div className="text-[11px] md:text-sm text-gray-500 py-2">and {count - 5} more</div>}
            </div>
          )}
          <div className="flex-1" />
          <div className="py-4">
            <button onClick={onReview} disabled={!count}
              className="w-full py-3 md:py-4 rounded-lg bg-navy text-white text-[13px] md:text-base font-semibold disabled:opacity-40">
              Review {count || ''} {count === 1 ? 'item' : 'items'} →
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function LastScan({ last }) {
  if (!last) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-3 md:p-4 text-center text-[12px] md:text-base text-gray-500">
        Point the camera at a label, or type a tag below.
      </div>
    );
  }
  if (last.result === 'added') {
    return (
      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 md:p-4">
        <div className="text-[11px] md:text-sm font-semibold text-emerald-800">✓ Added to basket</div>
        <div className="text-[15px] md:text-xl font-semibold text-emerald-900 mt-1">{last.tag}</div>
        <div className="text-[11px] md:text-sm text-emerald-800">
          {[last.item?.catalog_description || last.item?.name, last.item?.location_name, last.item?.allocated_sow_reference].filter(Boolean).join(' · ')}
        </div>
      </div>
    );
  }
  if (last.result === 'duplicate') {
    return (
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 md:p-4">
        <div className="text-[11px] md:text-sm font-semibold text-amber-800">{last.tag} is already in the basket</div>
      </div>
    );
  }
  return (
    <div className="rounded-lg bg-red-50 border border-red-200 p-3 md:p-4">
      <div className="text-[11px] md:text-sm font-semibold text-red-800">✗ {last.tag} is not a known label</div>
      <div className="text-[11px] md:text-sm text-red-700 mt-0.5">Check the tag and try again, or type it in.</div>
    </div>
  );
}

function BasketRow({ item, onRemove }) {
  const meta = [item.location_name, item.status ? STATUS_LABELS[item.status] || item.status : '', item.sow_reference].filter(Boolean).join(' · ');
  return (
    <div className="flex items-center justify-between py-2 md:py-3 border-b border-gray-100">
      <div className="min-w-0">
        <div className="text-[12px] md:text-base font-medium text-navy">{item.asset_tag}</div>
        <div className="text-[10px] md:text-sm text-gray-500 truncate">{[item.name, meta].filter(Boolean).join(' · ')}</div>
      </div>
      <button onClick={() => onRemove(item.asset_tag)} aria-label={`Remove ${item.asset_tag}`}
        className="text-gray-400 hover:text-red-600 px-2 md:px-3 md:py-2 text-base md:text-xl leading-none">✕</button>
    </div>
  );
}

// ── Camera in the page ───────────────────────────────────────────────────────
// Opens the rear camera with getUserMedia and reads QR labels from its frames.
// If the camera is refused or missing, the viewfinder collapses to a short note
// and the typed field (or the camera app round trip) still fills the basket.
//
// Chrome on Android has a built-in reader (BarcodeDetector). On the goods-in
// tablet it took about 40ms a frame against jsQR's 130ms, off the page's main
// thread, and caught labels jsQR missed. Safari has none, so iPhones keep jsQR.
async function makeQrDetector() {
  if (typeof window === 'undefined' || !('BarcodeDetector' in window)) return null;
  try {
    const formats = await window.BarcodeDetector.getSupportedFormats();
    return formats.includes('qr_code') ? new window.BarcodeDetector({ formats: ['qr_code'] }) : null;
  } catch (e) {
    return null;
  }
}

function CameraView({ onDecode }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [state, setState] = useState('idle');   // idle | starting | live | denied | unavailable
  const lastSeen = useRef({ text: null, at: 0 });
  const onDecodeRef = useRef(onDecode);
  useEffect(() => { onDecodeRef.current = onDecode; }, [onDecode]);

  // The camera opens from a tap rather than on mount. Entering this page is a
  // full load and scanning never needs the screen touched, so without this the
  // page had no gesture to unlock sound with on iOS.
  const started = state !== 'idle';
  useEffect(() => {
    if (!started) return undefined;
    let stream = null;
    let raf = 0;
    let stopped = false;
    const video = videoRef.current;
    if (!navigator.mediaDevices?.getUserMedia) { setState('unavailable'); return undefined; }

    let detector = null;
    const detectorReady = makeQrDetector().then(d => { detector = d; });

    const readFrame = async (v) => {
      if (detector) {
        try {
          const codes = await detector.detect(v);
          return codes[0]?.rawValue || null;
        } catch (e) {
          detector = null;   // a reader that fails is not trusted again: jsQR from here on
        }
      }
      const c = canvasRef.current;
      if (!c) return null;
      const w = Math.min(640, v.videoWidth);
      const h = Math.round(v.videoHeight * (w / v.videoWidth));
      if (c.width !== w) { c.width = w; c.height = h; }
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(v, 0, 0, w, h);
      const img = ctx.getImageData(0, 0, w, h);
      return jsQR(img.data, w, h, { inversionAttempts: 'dontInvert' })?.data || null;
    };

    // The next frame is read only once this one has been, so reads never queue up.
    const tick = async () => {
      if (stopped) return;
      const v = videoRef.current;
      if (v && v.readyState >= 2 && v.videoWidth) {
        const text = await readFrame(v);
        if (text && !stopped) {
          const now = Date.now();
          const same = lastSeen.current.text === text && now - lastSeen.current.at < REDECODE_MS;
          lastSeen.current = { text, at: now };
          if (!same) onDecodeRef.current(text);
        }
      }
      if (!stopped) raf = requestAnimationFrame(tick);
    };

    navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } }, audio: false })
      .then(s => {
        if (stopped) { s.getTracks().forEach(t => t.stop()); return; }
        stream = s;
        if (video) {
          video.srcObject = s;
          video.setAttribute('playsinline', 'true');
          video.play().catch(() => {});
        }
        setState('live');
        detectorReady.then(() => { if (!stopped) raf = requestAnimationFrame(tick); });
      })
      .catch(err => {
        setState(err?.name === 'NotAllowedError' ? 'denied' : 'unavailable');
      });

    return () => {
      stopped = true;
      if (raf) cancelAnimationFrame(raf);
      if (stream) stream.getTracks().forEach(t => t.stop());
    };
  }, [started]);

  if (state === 'denied' || state === 'unavailable') {
    return (
      <div className="mx-4 mt-3 md:mx-6 md:mt-4 rounded-lg bg-gray-100 border border-gray-200 p-3 md:p-4 text-[11px] md:text-sm text-gray-600">
        {state === 'denied'
          ? 'Camera access was refused. Allow it in the browser to scan here, or scan labels with the phone camera app and they will land in this basket.'
          : 'No camera available on this device. Type tags below, or scan labels with a phone and they will land in this basket.'}
      </div>
    );
  }
  return (
    // 4:3 on a phone, capped so the basket stays in view; from lg up it fills
    // the left of the screen beside the basket.
    <div className="relative bg-black aspect-[4/3] max-h-[38vh] md:max-h-[50vh] lg:aspect-auto lg:max-h-none lg:flex-1">
      <video ref={videoRef} muted playsInline className="w-full h-full object-cover" />
      <canvas ref={canvasRef} className="hidden" />
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="w-3/5 lg:w-auto lg:h-3/5 aspect-square border-2 rounded-lg" style={{ borderColor: '#E5B87A' }} />
      </div>
      {state === 'idle' && (
        <button type="button" onClick={() => { primeAudio(); setState('starting'); }}
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white">
          <span className="text-4xl md:text-6xl">📷</span>
          <span className="text-[13px] md:text-lg font-semibold">Tap to start scanning</span>
        </button>
      )}
      {state === 'starting' && (
        <div className="absolute inset-0 flex items-center justify-center text-white/80 text-[12px] md:text-base">Starting camera…</div>
      )}
    </div>
  );
}

// ── Screen 2: review and choose the action ──────────────────────────────────
function ReviewScreen({
  basket, locations, sows, actionKey, setActionKey, moveLoc, setMoveLoc, alsoInStock, setAlsoInStock,
  sowId, setSowId, notes, setNotes, needsSowOrReason, actionText, busy, onBack, onRemove, onApply,
}) {
  const count = basket.items.length;
  const usesSow = actionKey === 'allocated' || actionKey === 'deployed';
  return (
    <>
      <Header title={`${count} ${count === 1 ? 'item' : 'items'} scanned`} sub="Check the list, pick the action, apply" onLeft={onBack} />
      {/* From lg up the list and the action sit side by side, each scrolling on its own */}
      <div className="px-4 md:px-6 flex-1 flex flex-col lg:flex-row lg:gap-8 lg:min-h-0">
        <div className="max-h-[38vh] md:max-h-[45vh] lg:max-h-none lg:flex-1 lg:min-w-0 overflow-y-auto">
          {basket.items.map(i => <BasketRow key={i.asset_tag} item={i} onRemove={onRemove} />)}
          {count === 0 && <div className="text-[12px] md:text-base text-gray-500 py-6 text-center">The basket is empty. Go back and scan something.</div>}
        </div>

        <div className="flex-1 flex flex-col lg:flex-none lg:w-[26rem] lg:pl-8 lg:border-l border-gray-200 lg:overflow-y-auto">
          <div className="mt-3 pt-3 border-t lg:border-t-0 lg:mt-4 lg:pt-0 border-gray-200">
            <label className="block text-[10px] md:text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Action for all {count}</label>
            <select value={actionKey} onChange={e => setActionKey(e.target.value)} className="form-input text-[12px] md:text-base w-full">
              {BULK_ACTIONS.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
            </select>

            {actionKey === 'move' && (
              <>
                <label className="block text-[10px] md:text-xs font-bold text-gray-500 uppercase tracking-wide mt-3 mb-1">New location</label>
                <select value={moveLoc} onChange={e => setMoveLoc(e.target.value)} className="form-input text-[12px] md:text-base w-full">
                  <option value="">— Pick a location —</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  <option value="__none__">Unknown / no location</option>
                </select>
                <label className="flex items-center gap-2 text-[12px] md:text-base mt-3">
                  <input type="checkbox" checked={alsoInStock} onChange={e => setAlsoInStock(e.target.checked)} />
                  Also set status to In Stock
                </label>
              </>
            )}

            {usesSow && (
              <>
                <label className="block text-[10px] md:text-xs font-bold text-gray-500 uppercase tracking-wide mt-3 mb-1">SOW</label>
                <SowSelect sows={sows} value={sowId} onChange={setSowId} placeholder="— Not linked to a SOW —" inputClassName="form-input text-[12px] md:text-base w-full" />
              </>
            )}

            <label className="block text-[10px] md:text-xs font-bold text-gray-500 uppercase tracking-wide mt-3 mb-1">
              {needsSowOrReason ? 'Reason *' : 'Notes (optional)'}
            </label>
            <input value={notes} onChange={e => setNotes(e.target.value)} placeholder={usesSow ? 'e.g. lab rig, internal use' : 'e.g. Van 2, new storage'}
              className="form-input text-[12px] md:text-base w-full" />
            {needsSowOrReason && <div className="text-[11px] md:text-sm text-red-700 mt-1">Link a SOW or give a reason to {actionKey === 'allocated' ? 'allocate' : 'deploy'}.</div>}
          </div>

          <div className="flex-1" />
          <div className="py-4">
            <button onClick={onApply} disabled={busy || !count || (actionKey === 'move' && !moveLoc) || needsSowOrReason}
              className="w-full py-3 md:py-4 rounded-lg bg-navy text-white text-[13px] md:text-base font-semibold disabled:opacity-40">
              {busy ? 'Applying…'
                : (actionKey === 'move' && !moveLoc) ? 'Pick a location first'
                : `${actionText || 'Apply'}: ${count} ${count === 1 ? 'item' : 'items'}`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Screen 3: result ─────────────────────────────────────────────────────────
function DoneScreen({ outcome, busy, onUndo, onAnother, onExit }) {
  const skipped = outcome?.skipped || [];
  return (
    <>
      <Header title="Bulk scan" sub={outcome?.reference ? `Batch ${outcome.reference}` : ''} onLeft={onExit} leftIcon="x" />
      <div className="px-4 md:px-6 flex-1 flex flex-col md:w-full md:max-w-xl md:mx-auto lg:overflow-y-auto">
        <div className="text-center pt-6 md:pt-10 pb-3">
          <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full text-3xl ${outcome?.undone ? 'bg-gray-100 text-gray-500' : 'bg-emerald-50 text-emerald-600'}`}>
            {outcome?.undone ? '↺' : '✓'}
          </div>
          <div className="text-4xl font-semibold mt-3 tabular-nums">{outcome?.undone ? outcome.restored : outcome?.applied}</div>
          <div className="text-[13px] md:text-base text-gray-600 mt-1">
            {(outcome?.undone ? outcome.restored : outcome?.applied) === 1 ? 'item' : 'items'} {outcome?.undone ? 'put back where they were' : 'updated'}
          </div>
          {!outcome?.undone && outcome?.summary && (
            <div className="text-[13px] md:text-base font-semibold text-navy mt-1">{outcome.summary}</div>
          )}
        </div>
        {skipped.length > 0 && !outcome?.undone && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 my-2">
            <div className="text-[11px] md:text-sm font-semibold text-red-800">{skipped.length} skipped</div>
            {skipped.map(s => (
              <div key={s.asset_tag} className="text-[11px] md:text-sm text-red-700 mt-1">{s.asset_tag}: {s.reason}</div>
            ))}
          </div>
        )}
        <div className="text-[11px] md:text-sm text-gray-500 leading-relaxed mt-2">
          Recorded on each item's history with the batch reference, so the whole change can be traced or reversed together.
        </div>
        <div className="flex-1" />
        <div className="py-4 space-y-2">
          {!outcome?.undone && (
            <button onClick={onUndo} disabled={busy}
              className="w-full py-3 md:py-4 rounded-lg border border-gray-300 text-navy text-[13px] md:text-base font-semibold disabled:opacity-40">
              {busy ? 'Undoing…' : 'Undo this batch'}
            </button>
          )}
          <button onClick={onAnother} className="w-full py-3 md:py-4 rounded-lg bg-navy text-white text-[13px] md:text-base font-semibold">
            Start another bulk scan
          </button>
          <button onClick={onExit} className="w-full py-2 md:py-3 text-[12px] md:text-sm text-gray-500">Back to Inventory</button>
        </div>
      </div>
    </>
  );
}
